import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

// Corporate color: Dark blue/purple (#1a237e or similar)
const CORPORATE_COLOR_RGB = [26, 35, 126];

interface UserInfo {
  Name?: string;
  Mobile?: string;
  Email?: string;
  CEANumber?: string;
  Photo?: string;
}

interface AISummaryData {
  stats: {
    referenceUnit: {
      projectName: string;
      unitNumber: string;
      bedroom: number;
      bathroom: number;
      areaSqft: number;
    };
    pricePosition: string;
    pricePercentile: number;
    lessExpensive: number;
    moreExpensive: number;
    totalComparables: number;
    minPrice: number;
    medianPrice: number;
    maxPrice: number;
    refPrice: number;
    minPSF: number;
    medianPSF: number;
    maxPSF: number;
    refPSF: number;
    minOutlay: number;
    medianOutlay: number;
    maxOutlay: number;
    refOutlay: number;
    minInstallment: number;
    medianInstallment: number;
    maxInstallment: number;
    refInstallment: number;
    pros: string[];
    cons: string[];
    thumbsUpUnits?: Array<{
      unit: {
        projectName: string;
        unitNumber: string;
        bedroom: number;
        bathroom: number;
        areaSqft: number;
        price: number;
        psf: number;
        outlay: number;
        installment: number;
      };
      priceSavings: number;
      psfSavings: number;
      reasons: string[];
    }>;
  };
  radarData: Array<{
    metric: string;
    reference: number;
    median: number;
  }>;
  barData: Array<{
    name: string;
    Low: number;
    Median: number;
    High: number;
    Reference: number;
  }>;
}

export const generatePDFReport = async (
  aiSummaryData: AISummaryData,
  userInfo: UserInfo | null
): Promise<void> => {
  try {
    // Validate required data
    if (!aiSummaryData || !aiSummaryData.stats || !aiSummaryData.stats.referenceUnit) {
      throw new Error('Missing required data: aiSummaryData or referenceUnit');
    }

    if (!aiSummaryData.barData || !Array.isArray(aiSummaryData.barData)) {
      throw new Error('Missing or invalid barData');
    }

    if (!aiSummaryData.radarData || !Array.isArray(aiSummaryData.radarData)) {
      throw new Error('Missing or invalid radarData');
    }

    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  let currentPage = 1;
  const totalPages = 4; // We'll calculate this more accurately later, but estimate for now
  let yPos = 0; // Declare once at top level

  // Helper function to format currency
  const formatCurrency = (value: number): string => {
    return `$${value.toLocaleString('en-SG', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  // Helper function to format number
  const formatNumber = (value: number, decimals: number = 0): string => {
    return value.toLocaleString('en-SG', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  };

  // Helper function to parse unit number
  const parseUnitNumber = (unitNumber: string): { floor: number | null; stack: number | null } => {
    const match = unitNumber.match(/#?(\d+)-(\d+)/);
    if (match) {
      return {
        floor: parseInt(match[1], 10),
        stack: parseInt(match[2], 10),
      };
    }
    return { floor: null, stack: null };
  };

  // Add header to subsequent pages
  const addHeader = (title: string, pageNum: number, totalPages?: number) => {
    // Orange bar at top
    doc.setFillColor(255, 152, 0);
    doc.rect(0, 0, pageWidth, 8, 'F');
    
    // Blue header bar
    doc.setFillColor(CORPORATE_COLOR_RGB[0], CORPORATE_COLOR_RGB[1], CORPORATE_COLOR_RGB[2]);
    doc.rect(0, 8, pageWidth, 15, 'F');
    
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text(title, 10, 18);
    
    const pageText = totalPages ? `PAGE ${pageNum}/${totalPages}` : `PAGE ${pageNum}`;
    doc.text(pageText, pageWidth - 10, 18, { align: 'right' });
    
    doc.setTextColor(0, 0, 0);
  };
  
  // Add footer to pages
  const addFooter = (pageNum: number, totalPages?: number) => {
    const footerY = pageHeight - 15;
    doc.setFillColor(20, 30, 48);
    doc.rect(0, footerY, pageWidth, 15, 'F');
    
    // Left: Company name
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.text('Huttons - Singapore\'s Largest Private Real Estate Agency', 10, footerY + 8);
    
    // Center: Page number
    doc.text(`${pageNum}`, pageWidth / 2, footerY + 8, { align: 'center' });
    
    // Right: User name and CEA
    if (userInfo) {
      doc.setTextColor(255, 152, 0);
      doc.setFont('helvetica', 'bold');
      if (userInfo.Name) {
        doc.text(userInfo.Name, pageWidth - 10, footerY + 5, { align: 'right' });
      }
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'normal');
      if (userInfo.CEANumber) {
        doc.text(`CEA: ${userInfo.CEANumber}`, pageWidth - 10, footerY + 11, { align: 'right' });
      }
    }
  };

  // COVER PAGE - New Format
  // Dark blue/navy background for entire page
  doc.setFillColor(15, 23, 42); // Dark navy blue
  doc.rect(0, 0, pageWidth, pageHeight, 'F');
  
  // Top orange bar
  doc.setFillColor(255, 152, 0); // Orange
  doc.rect(0, 0, pageWidth, 8, 'F');
  
  // Company branding section
  yPos = 25;
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(24);
  doc.setFont('helvetica', 'bold');
  doc.text('HUTTONS', pageWidth / 2, yPos, { align: 'center' });
  
  yPos += 8;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('SINGAPORE\'S LARGEST PRIVATE REAL ESTATE AGENCY', pageWidth / 2, yPos, { align: 'center' });
  
  // Orange separator line
  yPos += 8;
  doc.setFillColor(255, 152, 0);
  doc.rect(pageWidth / 2 - 40, yPos, 80, 1, 'F');
  
  // Main title
  yPos += 25;
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(28);
  doc.setFont('helvetica', 'bold');
  doc.text('EXPERT MARKET ANALYSIS REPORT', pageWidth / 2, yPos, { align: 'center' });
  
  // User information block (centered)
  yPos += 40;
  const infoBoxWidth = 120;
  const infoBoxHeight = 50;
  const infoBoxX = (pageWidth - infoBoxWidth) / 2;
  const infoBoxY = yPos;
  
  // Info box background (slightly lighter than page background)
  doc.setFillColor(30, 41, 59); // Slightly lighter navy
  doc.setDrawColor(255, 152, 0); // Orange border
  doc.setLineWidth(2);
  doc.roundedRect(infoBoxX, infoBoxY, infoBoxWidth, infoBoxHeight, 3, 3, 'FD');
  
  if (userInfo) {
    // User photo (circular, left side of info box)
    // Position circle so it's properly contained within the box with some padding
    const photoRadius = 18;
    const photoPadding = 10; // Padding from left edge of box
    const photoX = infoBoxX + photoPadding + photoRadius; // Center X of circle
    const photoY = infoBoxY + infoBoxHeight / 2; // Center Y aligned with box center
    
    // Try to load user photo
    let photoLoaded = false;
    if (userInfo.Photo) {
      try {
        // Construct photo URL - ensure no double slashes
        const photoPath = userInfo.Photo.startsWith('/') ? userInfo.Photo.substring(1) : userInfo.Photo;
        const photoUrl = `https://portal.huttonsgroup.com/${photoPath}`;
        // Try to load image - jsPDF can load images if they're accessible
        // For CORS issues, we'll fall back to initials
        const img = new Image();
        img.crossOrigin = 'anonymous';
        
        await new Promise<void>((resolve, reject) => {
          const timeout = setTimeout(() => reject(new Error('Image load timeout')), 5000);
          img.onload = () => {
            clearTimeout(timeout);
            try {
              // Create a canvas to crop image to circle with proper aspect ratio (pixel-space to avoid distortion)
              const canvas = document.createElement('canvas');
              const ctx = canvas.getContext('2d');
              if (ctx) {
                const size = photoRadius * 2;
                const px = Math.max(256, Math.round(size * 12));
                canvas.width = px;
                canvas.height = px;
                ctx.imageSmoothingEnabled = true;
                ctx.imageSmoothingQuality = 'high';
                const r = px / 2;
                const maxInCircle = r * Math.SQRT2;
                const scale = Math.min(maxInCircle / img.width, maxInCircle / img.height);
                const sw = Math.round(img.width * scale);
                const sh = Math.round(img.height * scale);
                const sx = Math.round((px - sw) / 2);
                const sy = Math.round((px - sh) / 2);
                ctx.save();
                ctx.beginPath();
                ctx.arc(r, r, r, 0, Math.PI * 2);
                ctx.clip();
                ctx.drawImage(img, sx, sy, sw, sh);
                ctx.restore();
                const imgData = canvas.toDataURL('image/png');
                doc.addImage(imgData, 'PNG', photoX - photoRadius, photoY - photoRadius, size, size);
              } else {
                // Fallback: add image directly with proper aspect ratio
                const imgAspectRatio = img.width / img.height;
                let imgWidth = photoRadius * 2;
                let imgHeight = photoRadius * 2;
                
                if (imgAspectRatio > 1) {
                  // Image is wider - fit to height
                  imgWidth = imgHeight * imgAspectRatio;
                } else {
                  // Image is taller - fit to width
                  imgHeight = imgWidth / imgAspectRatio;
                }
                
                const imgX = photoX - imgWidth / 2;
                const imgY = photoY - imgHeight / 2;
                
                doc.addImage(img, 'JPEG', imgX, imgY, imgWidth, imgHeight, undefined, 'FAST');
              }
              
              // Draw circle border
              doc.setDrawColor(255, 152, 0);
              doc.setLineWidth(2);
              doc.circle(photoX, photoY, photoRadius, 'D');
              photoLoaded = true;
              resolve();
            } catch (err) {
              clearTimeout(timeout);
              reject(err);
            }
          };
          img.onerror = () => {
            clearTimeout(timeout);
            reject(new Error('Image failed to load'));
          };
          img.src = photoUrl;
        });
      } catch (error) {
        console.warn('Could not load user photo, using initials:', error);
        photoLoaded = false;
      }
    }
    
    // Fallback to initials circle if photo didn't load
    if (!photoLoaded) {
      doc.setFillColor(255, 152, 0);
      doc.circle(photoX, photoY, photoRadius, 'F');
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      const initials = userInfo.Name ? userInfo.Name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() : 'U';
      doc.text(initials, photoX, photoY + 3, { align: 'center' });
    }
    
    // User details (right side of info box)
    // Start text after the photo circle (which extends to photoX + photoRadius)
    const textStartX = infoBoxX + photoPadding + photoRadius * 2 + 15; // Start after photo + gap
    let detailY = infoBoxY + 12;
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    
    if (userInfo.Name) {
      doc.text(userInfo.Name, textStartX, detailY);
      detailY += 7;
    }
    
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    
    if (userInfo.CEANumber) {
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'normal');
      const ceaLabel = 'CEA Registration: ';
      doc.text(ceaLabel, textStartX, detailY);
      doc.setTextColor(255, 152, 0);
      doc.setFont('helvetica', 'bold');
      const ceaLabelWidth = doc.getTextWidth(ceaLabel);
      doc.text(userInfo.CEANumber, textStartX + ceaLabelWidth, detailY);
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'normal');
      detailY += 6;
    }
    
    if (userInfo.Email) {
      doc.text(userInfo.Email, textStartX, detailY);
      detailY += 6;
    }
    
    if (userInfo.Mobile) {
      doc.text(userInfo.Mobile, textStartX, detailY);
    }
  } else {
    // No user info - show placeholder
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('User information not available', infoBoxX + infoBoxWidth / 2, infoBoxY + infoBoxHeight / 2, { align: 'center' });
  }
  
  // Generation date
  yPos = infoBoxY + infoBoxHeight + 20;
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  const genDate = new Date().toLocaleDateString('en-SG', { year: 'numeric', month: 'long', day: 'numeric' });
  doc.text(`Generated on: ${genDate}`, pageWidth / 2, yPos, { align: 'center' });
  
  // Orange separator line
  yPos += 10;
  doc.setFillColor(255, 152, 0);
  doc.rect(pageWidth / 2 - 40, yPos, 80, 1, 'F');
  
  // Disclaimer
  yPos += 12;
  doc.setTextColor(200, 200, 200);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'italic');
  const disclaimer = 'This report is for informational purposes only. Please consult with a qualified professional for financial advice.';
  const disclaimerLines = doc.splitTextToSize(disclaimer, pageWidth - 40);
  doc.text(disclaimerLines, pageWidth / 2, yPos, { align: 'center' });
  
  // Footer
  const footerY = pageHeight - 15;
  doc.setFillColor(20, 30, 48); // Slightly lighter than main background
  doc.rect(0, footerY, pageWidth, 15, 'F');
  
  // Left: Company name
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.text('Huttons - Singapore\'s Largest Private Real Estate Agency', 10, footerY + 8);
  
  // Center: Page number
  doc.text('1', pageWidth / 2, footerY + 8, { align: 'center' });
  
  // Right: User name and CEA
  if (userInfo) {
    doc.setTextColor(255, 152, 0);
    doc.setFont('helvetica', 'bold');
    if (userInfo.Name) {
      doc.text(userInfo.Name, pageWidth - 10, footerY + 5, { align: 'right' });
    }
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'normal');
    if (userInfo.CEANumber) {
      doc.text(`CEA: ${userInfo.CEANumber}`, pageWidth - 10, footerY + 11, { align: 'right' });
    }
  }

  currentPage++;

  // PAGE 2: Summary Statistics
  doc.addPage();
  currentPage++;
  addHeader('SUMMARY REPORT', currentPage, totalPages);
  
  yPos = 30; // Start below header
  
  // Title
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text('Expert Market Analysis Report', pageWidth / 2, yPos, { align: 'center' });
  yPos += 8;
  
  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text(`${aiSummaryData.stats.referenceUnit.projectName || 'N/A'} ${aiSummaryData.stats.referenceUnit.unitNumber || 'N/A'}`, pageWidth / 2, yPos, { align: 'center' });
  yPos += 5;
  
  doc.setFontSize(8);
  doc.text('Analysis within same project (excluding sold units)', pageWidth / 2, yPos, { align: 'center' });
  yPos += 8;

  // Key Statistics Cards (simplified as tables)
  const statsData = [
    ['Price Position', `${(aiSummaryData.stats.pricePercentile || 0).toFixed(1)}%`, `${aiSummaryData.stats.pricePosition || 'N/A'} Tier`],
    ['Less Expensive', `${aiSummaryData.stats.lessExpensive || 0}`, 'Units'],
    ['More Expensive', `${aiSummaryData.stats.moreExpensive || 0}`, 'Units'],
    ['Total Comparables', `${aiSummaryData.stats.totalComparables || 0}`, 'Units Analyzed'],
  ];

  autoTable(doc, {
    startY: yPos,
    head: [['Metric', 'Value', 'Unit']],
    body: statsData,
    theme: 'striped',
    headStyles: { fillColor: CORPORATE_COLOR_RGB as [number, number, number], textColor: [255, 255, 255], fontStyle: 'bold' },
    styles: { fontSize: 9, textColor: [0, 0, 0] },
    margin: { left: 10, right: 10 },
  });

  yPos = (doc as any).lastAutoTable.finalY + 10;

  // Price Position Chip (visual representation)
  const pricePosition = aiSummaryData.stats.pricePosition || 'MEDIUM';
  const chipColor = pricePosition === 'LOW' ? [46, 125, 50] :
    pricePosition === 'HIGH' ? [211, 47, 47] :
    [255, 152, 0];
  doc.setFillColor(chipColor[0], chipColor[1], chipColor[2]);
  doc.rect(pageWidth / 2 - 30, yPos, 60, 8, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text(`${pricePosition} TIER`, pageWidth / 2, yPos + 5, { align: 'center' });
  doc.setTextColor(0, 0, 0);
  yPos += 15;

  // Statistical Comparison Bar Chart Data (as table)
  const barChartData = (aiSummaryData.barData || []).map(item => [
    item.name || '',
    formatCurrency(item.Low || 0),
    formatCurrency(item.Median || 0),
    formatCurrency(item.High || 0),
    formatCurrency(item.Reference || 0),
  ]);

  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Statistical Comparison: Low | Median | High | Reference Unit', 10, yPos);
  yPos += 8;

  autoTable(doc, {
    startY: yPos,
    head: [['Metric', 'Low', 'Median', 'High', 'Reference Unit']],
    body: barChartData,
    theme: 'striped',
    headStyles: { fillColor: CORPORATE_COLOR_RGB as [number, number, number], textColor: [255, 255, 255], fontStyle: 'bold' },
    columnStyles: {
      0: { fontStyle: 'bold', textColor: [0, 0, 0] },
      1: { halign: 'right', textColor: [46, 125, 50] },
      2: { halign: 'right', textColor: [255, 152, 0] },
      3: { halign: 'right', textColor: [211, 47, 47] },
      4: { halign: 'right', fontStyle: 'bold', textColor: CORPORATE_COLOR_RGB as [number, number, number] },
    },
    styles: { fontSize: 8, textColor: [0, 0, 0] },
    margin: { left: 10, right: 10 },
  });

  // Add footer to page 2
  addFooter(currentPage, totalPages);
  
  currentPage++;

  // PAGE 3: Market Statistics Summary Table
  doc.addPage();
  addHeader('SUMMARY REPORT', currentPage, totalPages);
  
  yPos = 30; // Start below header - reuse existing variable
  
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Market Statistics Summary', 10, yPos);
  yPos += 8;

  const marketStatsData = [
    ['Price', formatCurrency(aiSummaryData.stats.minPrice || 0), formatCurrency(aiSummaryData.stats.medianPrice || 0), formatCurrency(aiSummaryData.stats.maxPrice || 0), formatCurrency(aiSummaryData.stats.refPrice || 0)],
    ['PSF', formatCurrency(aiSummaryData.stats.minPSF || 0), formatCurrency(aiSummaryData.stats.medianPSF || 0), formatCurrency(aiSummaryData.stats.maxPSF || 0), formatCurrency(aiSummaryData.stats.refPSF || 0)],
    ['Outlay', formatCurrency(aiSummaryData.stats.minOutlay || 0), formatCurrency(aiSummaryData.stats.medianOutlay || 0), formatCurrency(aiSummaryData.stats.maxOutlay || 0), formatCurrency(aiSummaryData.stats.refOutlay || 0)],
    ['Installment', formatCurrency(aiSummaryData.stats.minInstallment || 0), formatCurrency(aiSummaryData.stats.medianInstallment || 0), formatCurrency(aiSummaryData.stats.maxInstallment || 0), formatCurrency(aiSummaryData.stats.refInstallment || 0)],
  ];

  autoTable(doc, {
    startY: yPos,
    head: [['Metric', 'Low', 'Median', 'High', 'Reference Unit']],
    body: marketStatsData,
    theme: 'striped',
    headStyles: { fillColor: CORPORATE_COLOR_RGB as [number, number, number], textColor: [255, 255, 255], fontStyle: 'bold' },
    columnStyles: {
      0: { fontStyle: 'bold' },
      1: { halign: 'right', textColor: [46, 125, 50] },
      2: { halign: 'right', textColor: [255, 152, 0] },
      3: { halign: 'right', textColor: [211, 47, 47] },
      4: { halign: 'right', fontStyle: 'bold', textColor: CORPORATE_COLOR_RGB as [number, number, number] },
    },
    styles: { fontSize: 9 },
    margin: { left: 10, right: 10 },
  });

  yPos = (doc as any).lastAutoTable.finalY + 15;

  // Pros and Cons
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  
  const bulletIndent = 4;
  const bulletLineHeight = 5;
  const columnWidth = pageWidth / 2 - 15;
  const columnPadding = 6;
  const columnTextWidth = columnWidth - columnPadding * 2 - bulletIndent;
  const columnGap = 10;
  const leftColumnX = 10;
  const rightColumnX = leftColumnX + columnWidth + columnGap;
  const cardRadius = 2.5;

  const drawCheckIcon = (x: number, y: number, size: number) => {
    doc.setDrawColor(255, 255, 255);
    doc.setLineWidth(0.6);
    doc.line(x, y + size * 0.55, x + size * 0.35, y + size * 0.9);
    doc.line(x + size * 0.35, y + size * 0.9, x + size, y + size * 0.2);
  };

  const drawWarningIcon = (x: number, y: number, size: number) => {
    doc.setDrawColor(255, 255, 255);
    doc.setLineWidth(0.6);
    doc.triangle(x, y + size, x + size / 2, y, x + size, y + size, 'S');
    doc.setFillColor(255, 255, 255);
    doc.rect(x + size * 0.47, y + size * 0.38, size * 0.06, size * 0.36, 'F');
    doc.circle(x + size / 2, y + size * 0.82, size * 0.06, 'F');
  };

  const getBulletLines = (items: string[]) =>
    items.flatMap((item) => doc.splitTextToSize(item, columnTextWidth));

  const renderBulletList = (items: string[], startX: number, startY: number) => {
    let y = startY;
    items.forEach((item) => {
      const lines = doc.splitTextToSize(item, columnTextWidth);
      const blockHeight = lines.length * bulletLineHeight;
      doc.text('•', startX, y);
      doc.text(lines, startX + bulletIndent, y);
      y += blockHeight + 1.5;
    });
    return y;
  };

  const strengths = Array.isArray(aiSummaryData.stats.pros) ? aiSummaryData.stats.pros : [];
  const considerations = Array.isArray(aiSummaryData.stats.cons) ? aiSummaryData.stats.cons : [];
  const strengthsLines = getBulletLines(strengths);
  const considerationsLines = getBulletLines(considerations);
  const headerHeight = 10;
  const contentHeight = Math.max(
    strengthsLines.length,
    considerationsLines.length,
    1
  ) * bulletLineHeight + 8;
  const cardHeight = headerHeight + contentHeight + columnPadding;

  const cardsTopY = (doc as any).lastAutoTable.finalY + 12;

  // Strengths card
  doc.setFillColor(76, 175, 80);
  doc.roundedRect(leftColumnX, cardsTopY, columnWidth, cardHeight, cardRadius, cardRadius, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  drawCheckIcon(leftColumnX + columnPadding, cardsTopY + 3.2, 5);
  doc.text('Strengths', leftColumnX + columnPadding + 8, cardsTopY + 7);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  renderBulletList(
    strengths,
    leftColumnX + columnPadding,
    cardsTopY + headerHeight + 4
  );

  // Considerations card
  doc.setFillColor(239, 83, 80);
  doc.roundedRect(rightColumnX, cardsTopY, columnWidth, cardHeight, cardRadius, cardRadius, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  drawWarningIcon(rightColumnX + columnPadding, cardsTopY + 2.6, 6);
  doc.text('Considerations', rightColumnX + columnPadding + 9, cardsTopY + 7);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  renderBulletList(
    considerations,
    rightColumnX + columnPadding,
    cardsTopY + headerHeight + 4
  );

  // Add footer to page 3
  addFooter(currentPage, totalPages);
  
  currentPage++;

  // PAGE 4: Reference Unit & Thumbs Up Units
  doc.addPage();
  addHeader('SUMMARY REPORT', currentPage, totalPages);
  
  yPos = 30; // Start below header - reuse existing variable
  
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('Reference Unit & Thumbs Up Units Comparison', 10, yPos);
  yPos += 10;

  // Reference Unit Row
  const unitParsed = parseUnitNumber(aiSummaryData.stats.referenceUnit.unitNumber || '');
  const referenceRow = [
    aiSummaryData.stats.referenceUnit.projectName || 'N/A',
    unitParsed.floor !== null ? unitParsed.floor.toString() : '-',
    unitParsed.stack !== null ? unitParsed.stack.toString() : '-',
    (aiSummaryData.stats.referenceUnit.bedroom || 0).toString(),
    (aiSummaryData.stats.referenceUnit.bathroom || 0).toString(),
    formatNumber(aiSummaryData.stats.referenceUnit.areaSqft || 0),
    formatCurrency(aiSummaryData.stats.refPrice || 0),
    formatCurrency(aiSummaryData.stats.refPSF || 0),
    formatCurrency(aiSummaryData.stats.refOutlay || 0),
    formatCurrency(aiSummaryData.stats.refInstallment || 0),
  ];

  const thumbsUpRows: string[][] = [];
  if (aiSummaryData.stats.thumbsUpUnits && Array.isArray(aiSummaryData.stats.thumbsUpUnits) && aiSummaryData.stats.thumbsUpUnits.length > 0) {
    aiSummaryData.stats.thumbsUpUnits.forEach((item) => {
      if (item && item.unit) {
        const itemParsed = parseUnitNumber(item.unit.unitNumber || '');
        thumbsUpRows.push([
          item.unit.projectName || 'N/A',
          itemParsed.floor !== null ? itemParsed.floor.toString() : '-',
          itemParsed.stack !== null ? itemParsed.stack.toString() : '-',
          (item.unit.bedroom || 0).toString(),
          (item.unit.bathroom || 0).toString(),
          formatNumber(item.unit.areaSqft || 0),
          formatCurrency(item.unit.price || 0),
          formatCurrency(item.unit.psf || 0),
          formatCurrency(item.unit.outlay || 0),
          formatCurrency(item.unit.installment || 0),
        ]);
      }
    });
  }

  autoTable(doc, {
    startY: yPos,
    head: [['Project', 'Floor', 'Stack', 'Bed', 'Bath', 'Area (sqft)', 'Price', 'PSF', 'Outlay', 'Installment']],
    body: [referenceRow, ...thumbsUpRows],
    theme: 'striped',
    headStyles: { fillColor: CORPORATE_COLOR_RGB as [number, number, number], textColor: [255, 255, 255], fontStyle: 'bold' },
    bodyStyles: { fontSize: 7 },
    columnStyles: {
      0: { cellWidth: 35 },
      6: { halign: 'right' },
      7: { halign: 'right' },
      8: { halign: 'right' },
      9: { halign: 'right' },
    },
    didParseCell: (data: any) => {
      if (data.row.index === 0) {
        // Reference unit row
        data.cell.styles.fillColor = CORPORATE_COLOR_RGB as [number, number, number];
        data.cell.styles.textColor = [255, 255, 255];
        data.cell.styles.fontStyle = 'bold';
      } else if (data.row.index > 0 && thumbsUpRows.length > 0) {
        // Thumbs up units
        data.cell.styles.fillColor = [200, 230, 200];
      }
    },
    styles: { fontSize: 7, textColor: [0, 0, 0] },
    margin: { left: 10, right: 10 },
  });

  // Expert Recommendation
  yPos = (doc as any).lastAutoTable.finalY + 15;
  if (yPos > pageHeight - 50) {
    addFooter(currentPage, totalPages);
    doc.addPage();
    currentPage++;
    addHeader('SUMMARY REPORT', currentPage, totalPages);
    yPos = 30;
  }

  doc.setFillColor(CORPORATE_COLOR_RGB[0], CORPORATE_COLOR_RGB[1], CORPORATE_COLOR_RGB[2]);
  doc.rect(10, yPos, pageWidth - 20, 25, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  const drawLightbulbIcon = (x: number, y: number, size: number) => {
    doc.setDrawColor(255, 255, 255);
    doc.setLineWidth(0.6);
    doc.circle(x + size * 0.5, y + size * 0.35, size * 0.3, 'S');
    doc.rect(x + size * 0.35, y + size * 0.62, size * 0.3, size * 0.18, 'S');
    doc.line(x + size * 0.4, y + size * 0.88, x + size * 0.6, y + size * 0.88);
  };
  drawLightbulbIcon(12, yPos + 3.5, 7);
  doc.text('Expert Recommendation', 21, yPos + 8);
  
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  const refPrice = aiSummaryData.stats.refPrice || 0;
  const medianPrice = aiSummaryData.stats.medianPrice || 0;
  const lessExpensive = aiSummaryData.stats.lessExpensive || 0;
  const recommendation = refPrice <= medianPrice
    ? 'Strong value proposition — This unit is priced below the median, offering good value relative to the market.'
    : refPrice > medianPrice && lessExpensive > 0
    ? 'Premium pricing — While priced above the median, this unit may offer superior location, views, or other premium features.'
    : 'Market aligned — Pricing is consistent with market expectations based on comparable units.';
  
  const recLines = doc.splitTextToSize(recommendation, pageWidth - 30);
  doc.text(recLines, 12, yPos + 15);

  // Footer Disclaimer
  yPos += 30;
  if (yPos > pageHeight - 40) {
    addFooter(currentPage, totalPages);
    doc.addPage();
    currentPage++;
    addHeader('SUMMARY REPORT', currentPage, totalPages);
    yPos = 30;
  }

  const drawDisclaimerIcon = (x: number, y: number, size: number) => {
    doc.setFillColor(255, 152, 0);
    doc.setDrawColor(255, 152, 0);
    doc.triangle(x, y + size, x + size / 2, y, x + size, y + size, 'F');
    doc.setFillColor(255, 255, 255);
    doc.rect(x + size * 0.47, y + size * 0.35, size * 0.06, size * 0.36, 'F');
    doc.circle(x + size / 2, y + size * 0.8, size * 0.06, 'F');
  };

  doc.setTextColor(255, 152, 0);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  drawDisclaimerIcon(10, yPos - 3, 5);
  doc.text('Disclaimer', 17, yPos);
  doc.setTextColor(0, 0, 0);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.text('* This is just an AI generated view, and does not represent a professional advisor view. Please consult your advisor accordingly.', 10, yPos + 6, { maxWidth: pageWidth - 20 });

  // Add footer to last page
  addFooter(currentPage, totalPages);
  
  // Additional footer text above the footer bar
  yPos = pageHeight - 25;
  doc.setFontSize(7);
  doc.setTextColor(200, 200, 200);
  doc.text(`Report generated on ${new Date().toLocaleDateString('en-SG', { year: 'numeric', month: 'long', day: 'numeric' })}`, pageWidth / 2, yPos, { align: 'center' });
  doc.text(`Analysis based on ${aiSummaryData.stats.totalComparables || 0} comparable units within the same project (excluding sold units)`, pageWidth / 2, yPos + 4, { align: 'center' });

    // Save PDF
    const projectName = (aiSummaryData.stats.referenceUnit.projectName || 'Unknown').replace(/\s+/g, '_');
    const unitNumber = (aiSummaryData.stats.referenceUnit.unitNumber || 'Unknown').replace(/#/g, '');
    const fileName = `Expert_Market_Analysis_${projectName}_${unitNumber}_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  } catch (error) {
    console.error('PDF Generation Error:', error);
    throw error;
  }
};
